import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config';
import endpoints from '../../../services/endpoints';
import { jwtDecode } from 'jwt-decode';
import { Search, DollarSign, Calendar, Clock, AlertCircle, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../_newprofiles/components/ui/Card';
import { Badge } from '../../../_newprofiles/components/ui/Badge';
import { Button } from '../../../_newprofiles/components/ui/Button';
import styles from './ModernViewJobList.module.css';
import Swal from 'sweetalert2';
import { checkExistingQuote } from '../../../components/Message/Services/checkExistingQuote';
import DummyUploadImage from "../../../assets/images/upload-image5.svg";

const ModernViewJobList = ({ setSubscription }) => {
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [businessUserId, setBusinessUserId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isQuoteSent, setIsQuoteSent] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      const decodedToken = jwtDecode(token);
      setBusinessUserId(decodedToken.nameid);
      fetchJobs(decodedToken.BusinessId);
    }
  }, []);

  const fetchJobs = async (businessId) => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/${endpoints.getAllJobsForBusiness}?businessId=${businessId}`);
      if (response.data === 'Subscription Expired !!') {
        setSubscription(true);
        setJobs([]);
      } else {
        setSubscription(false);
        setJobs(response.data);
        if (response.data.length > 0) {
          handleJobClick(response.data[0]);
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleJobClick = async (job) => {
    setSelectedJob(job);
    const quoteExists = await checkExistingQuote(job.job_id, businessUserId);
    setIsQuoteSent(quoteExists);
  };

  const filteredJobs = jobs.filter((job) =>
    job.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (isLoading) {
    return <div className={styles.loading}>Loading jobs...</div>;
  }

  if (error) {
    return <div className={styles.error}>Error: {error}</div>;
  }

  return (
    <div className={styles.layoutGrid}>
      {/* Left Column: Job List */}
      <div className={styles.leftColumn}>
        <Card className={styles.card}>
          <CardHeader>
            <CardTitle>Matching Jobs</CardTitle>
            <div className={styles.searchContainer}>
              <Search className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search by keyword..."
                className={styles.searchInput}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </CardHeader>
          <CardContent className={styles.jobList}>
            {filteredJobs.length > 0 ? (
              filteredJobs.map((job) => (
                <div
                  key={job.job_id}
                  className={`${styles.jobListItem} ${selectedJob?.job_id === job.job_id ? styles.selected : ''}`}
                  onClick={() => handleJobClick(job)}
                >
                  <h4 className={styles.jobTitle}>{job.name}</h4>
                  <p className={styles.jobLocation}>{job.postcode}</p>
                  <Badge variant={job.isNew ? "destructive" : "secondary"}>
                    {job.isNew ? 'New' : 'Viewed'}
                  </Badge>
                </div>
              ))
            ) : (
              <p>No jobs found.</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Right Column: Job Details */}
      <div className={styles.rightColumn}>
        {selectedJob ? (
          <Card className={styles.card}>
            <CardHeader className={styles.jobDetailHeader}>
              <CardTitle>{selectedJob.name}</CardTitle>
              {!isQuoteSent ? (
                <Button>
                  <DollarSign className={styles.buttonIcon} />
                  Quote Now
                </Button>
              ) : (
                <Button variant="secondary" disabled>
                  <CheckCircle className={styles.buttonIcon} />
                  Already Quoted
                </Button>
              )}
            </CardHeader>
            <CardContent>
              <p className={styles.jobDescription}>{selectedJob.description}</p>
              <div className={styles.photoGrid}>
                {[selectedJob.image1, selectedJob.image2, selectedJob.image3, selectedJob.image4].map((img, index) => (
                  <div key={index} className={styles.photoContainer}>
                    <img src={img || DummyUploadImage} alt={`Job photo ${index + 1}`} className={styles.photo} />
                  </div>
                ))}
              </div>
              <div className={styles.detailGrid}>
                <div className={styles.detailItem}>
                  <DollarSign className={styles.detailIcon} />
                  <div>
                    <p className={styles.detailLabel}>Budget</p>
                    <p className={styles.detailValue}>$ {selectedJob.budgetAmount || 'N/A'}</p>
                  </div>
                </div>
                <div className={styles.detailItem}>
                  <Calendar className={styles.detailIcon} />
                  <div>
                    <p className={styles.detailLabel}>Deadline</p>
                    <p className={styles.detailValue}>{selectedJob.deadline || 'N/A'}</p>
                  </div>
                </div>
                <div className={styles.detailItem}>
                  <Clock className={styles.detailIcon} />
                  <div>
                    <p className={styles.detailLabel}>Site Visit</p>
                    <p className={styles.detailValue}>{selectedJob.siteVisit || 'N/A'}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className={styles.noJobSelected}>
            <AlertCircle className={styles.noJobIcon} />
            <p>Select a job to view details</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ModernViewJobList;
